/* What has happened to the subscription — STORY-032, STORY-034.
 *
 * The read side of the trail, and the reason it exists: both stories require
 * *"why did we lose access on the 3rd"* to be answerable, and until there was a
 * screen the answer was written down where nobody could reach it. An audit
 * trail with no read path is half a feature — it satisfies the letter of the
 * criterion and none of its point.
 *
 * ── it shows two kinds of line, and the pair is the answer ───────────────────
 * A **transition** is the provider moving the state. A **request** is somebody
 * here asking for it. They happen at different times — an Owner cancels on the
 * 1st and it ends on the 3rd — so a list showing only one kind explains
 * neither.
 *
 * It is also how a cancellation nobody here asked for becomes visible: a
 * transition with no request above it. Stripe refunds inside 60 days without
 * consulting us and cancels on a customer's data-deletion request, and this is
 * where that shows up rather than in a support conversation.
 */

import type { SubscriptionChange } from '@eventuley/contracts';
import { formatDate } from '@eventuley/i18n';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';

/* Explicit maps, never a constructed key — `i18n.md` forbids
 * `t('billing.history.' + action)`, which defeats extraction and unused-key
 * detection. */
const REQUEST_LABEL = {
  checkout_started: 'billing.history.checkout_started',
  cancellation_requested: 'billing.history.cancellation_requested',
  cancellation_lifted: 'billing.history.cancellation_lifted',
  transitioned: 'billing.history.transitioned',
} as const;

const STATUS_LABEL = {
  incomplete: 'billing.status_incomplete',
  active: 'billing.status_active',
  past_due: 'billing.status_past_due',
  unpaid: 'billing.status_unpaid',
  canceled: 'billing.status_canceled',
} as const;

export interface BillingHistoryProps {
  readonly changes: readonly SubscriptionChange[] | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
}

export function BillingHistory({ changes, isPending, isError }: BillingHistoryProps) {
  const t = useTranslate();
  const { formatLocale } = useI18n();
  /* The VIEWER's zone. `i18n.md` reserves the venue-timezone rule for an
   * Event's own time; when a subscription changed is ours and belongs in the
   * reader's. */
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <section className="billing__history">
      <h2>{t('billing.history.heading')}</h2>

      {isPending ? <p className="field__hint">{t('billing.history.loading')}</p> : null}

      {/* An error and an empty trail must not look the same — a workspace told
          nothing has happened when the request merely failed would conclude the
          record does not exist. */}
      {isError ? <p className="field__hint">{t('billing.history.unavailable')}</p> : null}

      {!isPending && !isError && changes?.length === 0 ? (
        <p className="field__hint">{t('billing.history.empty')}</p>
      ) : null}

      {changes && changes.length > 0 ? (
        <ol className="billing__history-list">
          {changes.map((change) => (
            <li key={`${change.at}-${change.action}-${change.toStatus ?? ''}`}>
              <span className="billing__history-what">
                {change.action === 'transitioned' && change.toStatus
                  ? t('billing.history.moved_to', {
                      status: t(STATUS_LABEL[change.toStatus]),
                    })
                  : t(REQUEST_LABEL[change.action])}
              </span>{' '}
              <span className="billing__history-who">
                {/* Absent actor means the PROVIDER acted, which is most lines.
                    Saying so beats leaving a blank: "who did this" is the
                    question, and "not one of us" is a real answer. */}
                {change.actor === null
                  ? t('billing.history.by_provider')
                  : t('billing.history.by', {
                      /* Erased accounts keep the row and lose the name —
                         ADR-0015. The label is rendered here rather than stored,
                         so a Turkish reader sees Turkish. */
                      who: change.actor.name || t('billing.history.by_erased'),
                    })}
              </span>{' '}
              <time className="billing__history-when" dateTime={change.at}>
                {formatDate(new Date(change.at), { locale: formatLocale, timeZone })}
              </time>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
